/* eslint-disable no-param-reassign */
import React from 'react';
import { isArray, startCase, isObject } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import noImage from '../../assets/images/no-image.png';
import { checkArray, roundingOff, imageUrl, shortenandHoverString } from '../../Components/helper';
import imageRemoved from '../../assets/images/image-removed.png';
import { alignPomRowsByCode, formatBaseSizePomGradings, formatCadFileFields, formatWeightVolumeRows, hasGradingRunChanged } from './baseSizeHistoryUtils';

const SPEC_POM_LABEL = 'Other, Point of Measure';
const CAD_FILE_POM_LABEL = 'CAD File, Point of Measure';

const FIELD_DISPLAY_NAMES = {
  colourwayId: 'Colourway',
  pdfExcludedComment: 'Design Detail Note',
  baseSizeSpecPomLibraries: SPEC_POM_LABEL,
  baseSizePomLibraries: CAD_FILE_POM_LABEL,
  internalComment: 'Comments',
};

const BASE_SIZE_POM_FIELDS = ['baseSizeSpecPomLibraries', 'baseSizePomLibraries'];

const getFieldDisplayName = (fieldName) => FIELD_DISPLAY_NAMES[fieldName] || startCase(fieldName);

function DevelopmenorderFields({ historyFieldsData, colType, keyToDisplay }) {
  const { t } = useTranslation('common');
  function isIsoDate(str) {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
    const d = new Date(str);
    return d instanceof Date && !Number.isNaN(d) && d.toISOString() === str; // valid date
  }
  const imgSrc = (e) => {
    console.log('imgSrc e', e);
    if (e !== 'Image changed') {
      if (typeof e === 'string' && e.startsWith('https://') && (e.includes('?') || e.includes('X-Amz-Signature') || e.includes('AWSAccessKeyId'))) {
        return e;
      }
      if (typeof e === 'object') {
        if (e?.image && e?.image?.length > 0) {
          return `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${e?.image}`;
        }
        return noImage;
      }
      if (e?.includes('data:image')) {
        return e;
      }
      return e ? `${imageUrl}/${e}` : noImage;
    }
  };

  const dateFormat = 'DD-MMM-YYYY';

  const downloadUrl = (path, fileName) => {
    const fileURL = `${imageUrl}/${path}`;
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = fileName;
    link.click();
  };

  const renderProductIndentifier = (fields) => {
    return (
      <>
        <b>{startCase(fields.field)}</b>:<br />{' '}
        {fields?.[colType] &&
          fields[colType].map((prodInden) => {
            const dispalyObject = Object.keys(keyToDisplay)
              .filter((key) => key in prodInden)
              .reduce((obj, key) => {
                obj[keyToDisplay[key]] =
                  prodInden[key] && typeof prodInden[key] === 'object' && 'name' in prodInden[key] ? prodInden[key].name : prodInden[key];
                return obj;
              }, {});
            return (
              <>
                {Object.entries(dispalyObject)
                  .filter(([key]) => key !== 'Product number') // Filter out "Product number"
                  .map(([key, value]) => (
                    <>
                      <b>{startCase(key)}</b> : {value || '--'}
                      <br />
                    </>
                  ))}

                <hr />
              </>
            );
          })}
      </>
    );
  };

  function getTimeFromString(dateString) {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    });
  }
  const getCheckboxValue = (columnValue) => {
    return (
      columnValue?.columnValue &&
      Object.keys(columnValue?.columnValue)
        .filter((key) => columnValue?.columnValue[key])
        .map((key) => columnValue?.options?.[key]?.name)
        .join(', ')
    );
  };

  const getRadioButtonValue = (columnValue) => {
    return columnValue?.options?.find((option) => columnValue?.columnValue?.toString() === option?._id?.toString())?.name;
  };

  const getMultiSelectDropdownValue = (columnValue) => {
    const columnData = columnValue?.columnValue;
    if (columnData?.length > 1) {
      return columnValue?.options
        ?.filter((option) => columnData?.includes(option?._id?.toString()))
        .map((option) => option?.name)
        .join(', ');
    }
    if (columnData?.length === 1) {
      return columnValue?.options?.find((option) => columnData?.includes(option?._id?.toString()))?.name;
    }
    return '';
  };
  const getColumnValue = (columnValue) => {
    const { key } = columnValue?.manualType || {};
    const columnData = columnValue?.columnValue;
    switch (key) {
      case 'Time_picker':
        return getTimeFromString(columnData);
      case 'Date_picker':
        return moment.utc(columnData).format('DD-MMMM-YYYY');
      case 'Checkbox':
        return getCheckboxValue(columnValue);
      case 'Radio_button':
        return getRadioButtonValue(columnValue);
      case 'HTML':
        return columnData?.replace(/<[^>]+?>/g, '');
      case 'Dropdown':
        return columnData?.name;
      case 'Multi_select_Dropdown':
        return getMultiSelectDropdownValue(columnValue);
      default:
        return columnData;
    }
  };

  const getManualColumnValue = (colData) => {
    return colData?.reduce((result, columnValue, index) => {
      const name = columnValue?.name;
      const value = getColumnValue(columnValue);
      if (value) {
        if (index === 0) {
          result += `${name}: ${value}`;
        } else {
          result += `, ${name}: ${value}`;
        }
      }

      return result;
    }, '');
  };

  const getDisplayValue = (key) => {
    switch (key) {
      case 'colourWay':
      case 'colourId':
        return `${t('PRODUCT_HISTORY.COLOURWAY')}`;
      case 'lengthId':
        return `${t('PRODUCT_HISTORY.LENGTH')}`;
      case 'importedProducts':
        return `${t('PRODUCT_HISTORY.PRODUCT_NUMBER')}`;
      case 'columns':
        return `${t('PRODUCT_HISTORY.COLUMN_VALUE')}`;
      default:
        return startCase(key);
    }
  };

  const getDisplayValueResult = (displayValue, value) => {
    if (displayValue === 'Column Value') {
      return getManualColumnValue(value);
    }
    if (typeof value === 'object') {
      return value?.name;
    }
    return value;
  };

  const renderProductInden = (prodInden) => {
    return Object.entries(prodInden).map(([key, value]) => {
      const displayValue = getDisplayValue(key);
      const displayValueResult = getDisplayValueResult(displayValue, value);

      return (
        displayValue !== 'Id' && (
          <div key={key}>
            <b>{displayValue}</b>: {displayValueResult}
            <br />
          </div>
        )
      );
    });
  };

  const renderVariants = (fields) => {
    return fields[colType] && Array.isArray(fields[colType]) ? (
      <>
        <b>{startCase(fields.field)}</b>:<br />
        {fields[colType] &&
          fields[colType].length > 0 &&
          fields[colType].map((prodInden) => (
            <div key={Math.floor(Math.random() * 5)}>
              {renderProductInden(prodInden)}
              <hr />
            </div>
          ))}
      </>
    ) : (
      <div>{fields[colType]}</div>
    );
  };

  const getManualValue = (colData) => {
    let value;
    if (colData?.manualType?.key === 'Time_picker') {
      value = getTimeFromString(colData?.columnValue);
    } else if (colData?.manualType?.key === 'Date_picker') {
      value = moment.utc(colData?.columnValue).format('DD-MMMM-YYYY');
    } else if (colData?.manualType?.key === 'Checkbox') {
      value = Object.keys(colData?.columnValue)
        .filter((key) => colData?.columnValue[key])
        .map((key) => colData?.options?.[key]?.name)
        .join(', ');
    } else if (colData?.manualType?.key === 'Radio_button') {
      value = colData?.columnValue ? colData?.options?.find((option) => colData?.columnValue?.toString() === option?._id?.toString())?.name : '';
    } else if (colData?.manualType?.key === 'HTML') {
      value = colData?.columnValue.replace(/<[^>]+>/g, '');
    } else if (colData?.manualType?.key === 'Dropdown') {
      value = colData?.columnValue?.name;
    } else if (colData?.manualType?.key === 'Multi_select_Dropdown' || colData?.manualType?.key === 'Multi-select Dropdown') {
      if (colData?.columnValue?.length > 1) {
        value = colData?.options
          .filter((option) => colData?.columnValue?.includes(option?._id.toString()))
          .map((option) => option?.name)
          .join(', ');
      } else if (colData?.columnValue?.length === 1) {
        value = colData?.options.find((option) => colData?.columnValue.includes(option._id.toString())).name;
      }
    } else {
      value = colData?.columnValue;
    }

    return value;
  };

  const groupedData = (data) => {
    const findData = data.reduce((acc, obj) => {
      if (!acc[obj.name]) {
        acc[obj.name] = { columnName: obj.name, values: [] };
      }
      acc[obj.name].values.push(obj);
      return acc;
    }, {});

    // Create an array to hold the JSX elements
    const elements = [];

    // Iterate over the keys of findData and create JSX elements
    Object.keys(findData).forEach((columnId) => {
      elements.push(
        <div key={Math.floor(Math.random() * 5)}>
          <h2 className="column-title">{findData[columnId].columnName}</h2>
          <ul>
            {findData[columnId].values.map((value) => (
              <li key={Math.floor(Math.random() * 4)}>{getManualValue(value)}</li>
            ))}
          </ul>
        </div>,
      );
    });

    // Return the array of JSX elements wrapped in a div
    return <div>{elements}</div>;
  };

  const renderManualVariants = (fields) => {
    return fields[colType] && Array.isArray(fields[colType]) ? (
      <>
        <b>{t('PRODUCT_HISTORY.PRODUCT_LIST_DATA')}</b>
        <br />
        <b>{startCase(fields.field)}</b>:<br />
        {fields[colType].map((prodInden) => (
          <>
            {Object.entries(prodInden).map(([key, value]) => {
              let displayValue;
              switch (key) {
                case 'colourId':
                  displayValue = 'Colour';
                  break;
                case 'lengthId':
                  displayValue = 'Length';
                  break;
                case 'columns':
                  displayValue = 'Columns';
                  break;
                default:
                  displayValue = startCase(key);
                  break;
              }
              let displayValueResult;
              if (displayValue === 'Columns') {
                displayValueResult = groupedData(value);
              } else if (typeof value === 'object') {
                displayValueResult = value?.name;
              } else {
                displayValueResult = value;
              }
              return (
                <div key={key.toString()}>
                  {key !== '_id' && (
                    <>
                      <b>{displayValue}</b>: {displayValueResult}
                      <br />
                    </>
                  )}
                </div>
              );
            })}
            <hr />
          </>
        ))}
      </>
    ) : (
      <div>{fields[colType]}</div>
    );
  };

  const getDynamicValue = (dyn) => {
    let result;
    if (Array.isArray(dyn?.valueId)) {
      result = dyn.valueId.map((val) => val?.keyName || val?.label || val).join(', ');
    } else if (Array.isArray(dyn?.valueIds)) {
      result = dyn.valueIds.map((val) => val?.keyName || val?.label || val).join(', ');
    } else if (dyn?.valueId?.keyName) {
      result = dyn.valueId.keyName;
    } else if (dyn?.valueText) {
      result = dyn.valueText;
    } else {
      result = '--';
    }
    return result;
  };
  const renderDynamicColumn = (fields) => {
    const colArray = Array.isArray(fields?.[colType]) ? fields[colType] : [];

    const activeColumns = colArray.filter((dyn) => dyn?.colId?.isActive !== false);

    if (!activeColumns?.length) return null;

    return activeColumns.map((dyn) => (
      <div key={dyn?.colId?._id || dyn?.colId}>
        <b>{startCase(dyn?.colId?.keyName)}</b>: {getDynamicValue(dyn)}
      </div>
    ));
  };

  const renderComment = (fields) => {
    return (
      <>
        <b>{startCase(fields.field)}</b>: {fields?.[colType] && <div dangerouslySetInnerHTML={{ __html: fields?.[colType] }} />}
      </>
    );
  };

  const handleSecondCondition = (comp) => {
    return !isArray(comp?.value) && isObject(comp?.value)
      ? Object?.keys(comp?.value)
          ?.map((key) => `${comp?.value[key]}`)
          .join(', ')
      : comp?.value;
  };
  const renderData = (fields) => {
    return (
      <>
        <b>{startCase(fields.field)}</b>:{' '}
        {fields?.[colType] &&
          fields[colType]?.map((prodInden) => {
            const keysMap = ['columns'];
            return (
              keysMap &&
              Array.isArray(keysMap) &&
              keysMap.map((keysss) => {
                return (
                  <>
                    <b>
                      {startCase(keysss)}: {`${prodInden?.colourWay?.name || '--'}/${prodInden?.size || '--'}/${prodInden?.length?.name || '--'}`}
                    </b>{' '}
                    :{' '}
                    {prodInden[keysss]
                      ?.map((comp) => {
                        return `${comp?.columnId?.title}/${isIsoDate(comp?.value) ? moment(comp?.value).format(dateFormat) : handleSecondCondition(comp)}`;
                      })
                      .join(', ')}
                    <br />
                  </>
                );
              })
            );
          })}
      </>
    );
  };

  const renderImage = (fields) => {
    const lhsImages = Array.isArray(fields?.lhs) ? fields.lhs : [];
    const rhsImages = Array.isArray(fields?.rhs) ? fields.rhs : [];

    const isComparisonView = colType === 'lhs' || colType === 'rhs';

    if (isComparisonView) {
      const displayImages = colType === 'lhs' ? lhsImages : rhsImages;
      return (
        <>
          <b>{startCase(fields.field)}</b>:{' '}
          <div className="d-flex flex-wrap gap-2 mt-1">
            {displayImages.length > 0 ? (
              displayImages.map((img, idx) => (
                <div key={`${colType}-${idx}`} className="image-slot">
                  {img && img !== 'Image changed' ? (
                    <img
                      alt={`${fields.field} ${colType} ${idx + 1}`}
                      width="48product-history/"
                      height="48"
                      src={imgSrc(img)}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = imageRemoved;
                      }}
                    />
                  ) : (
                    <div
                      className="gray-placeholder"
                      style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: '#e0e0e0',
                        border: '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '2px',
                      }}
                    >
                      <span style={{ color: '#999', fontSize: '10px' }}>Empty</span>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <span>--</span>
            )}
          </div>
        </>
      );
    }

    let currentImages = [];
    if (Array.isArray(fields[colType])) {
      currentImages = fields[colType];
    } else if (fields[colType] && fields[colType] !== 'Image changed') {
      currentImages = [fields[colType]];
    }

    return (
      <>
        <b>{startCase(fields.field)}</b>:{' '}
        {currentImages.length > 0 ? (
          <div className="d-flex flex-wrap gap-2 mt-1">
            {currentImages.map((img, idx) => (
              <div key={`${colType}-${idx}`} className="image-slot">
                {img && img !== 'Image changed' ? (
                  <img
                    alt={`${fields.field} ${idx + 1}`}
                    width="70"
                    height="70"
                    src={imgSrc(img)}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = imageRemoved;
                    }}
                  />
                ) : (
                  <div
                    className="gray-placeholder"
                    style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#e0e0e0',
                      border: '1px solid #ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '2px',
                    }}
                  >
                    <span style={{ color: '#999', fontSize: '10px' }}>Empty</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          '--'
        )}
        <br />
      </>
    );
  };

  const renderPdf = (fields) => {
    return (
      <>
        <b>{startCase(fields.field)}</b>:{' '}
        <button aria-label="btn-download" type="button" className="btn" onClick={() => downloadUrl(fields[colType], '')} title="Download File">
          <span className="icon-download" />
        </button>
        <br />
      </>
    );
  };

  const renderTrackingDates = (fields) => {
    return (
      <>
        <b>{startCase(fields?.field)}</b>: <br />
        {fields?.[colType] &&
          Array.isArray(fields?.[colType]) &&
          fields[colType]?.map((dates) => {
            return (
              <>
                <b>{dates?.label}</b>: {dates?.value ? moment(dates?.value).format(dateFormat) : '--'}
                <br />
              </>
            );
          })}
        <br />
      </>
    );
  };

  const renderColourways = (fields) => {
    return (
      <>
        <b>{startCase(fields?.field)}</b>:{' '}
        {fields?.[colType] &&
          Array.isArray(fields?.[colType]) &&
          fields?.[colType]?.map((col) => {
            return (
              <>
                {col?.colourway?.name || col?.name || ''}
                {col?.size ? `/${col?.size}` : ''}
                {col?.length?.name ? `/${col?.length?.name}` : ''}
                {col?.quantity ? `/${col?.quantity}` : ''}
                {col?.uom?.name ? `/${col?.uom?.name}` : ''}
                <br />
              </>
            );
          })}
      </>
    );
  };

  const renderBomCompoentn = (fields) => {
    return (
      <>
        <b>{startCase(fields?.field)}</b>: {`${fields[colType]?.referenceCode || '--'}`}
        <br />
      </>
    );
  };

  const renderComposition = (fields) => {
    return (
      <>
        <b>{startCase(fields?.field)}</b>:{' '}
        {checkArray(fields[colType])
          ? fields[colType]
              ?.map((comp) => {
                return `${comp?.percentage}% ${comp?.composition?.name}`;
              })
              .join(', ')
          : '--'}
        <br />
      </>
    );
  };

  /**
   * Renders one side of a Specifications POM row, walking both sides in a single aligned
   * order so line N of the From column and line N of the To column name the same POM.
   * @param {Object} fields one history field entry, carrying both lhs and rhs.
   * @returns {JSX.Element}
   */
  const renderSpecPom = (fields) => {
    return (
      <>
        <b>{startCase(fields.field)}</b>:{' '}
        {alignPomRowsByCode(fields?.lhs, fields?.rhs).map((alignedRow) => {
          const dyn = alignedRow[colType];
          if (!dyn) {
            return (
              <React.Fragment key={alignedRow.key}>
                <b>--</b>
                <br />
              </React.Fragment>
            );
          }
          const gradings = dyn?.gradings || [];
          const measurementUnit = dyn?.measurementUnit || null;
          if (isArray(gradings) && gradings.length > 0 && dyn?.code) {
            return (
              <React.Fragment key={alignedRow.key}>
                <b>{dyn?.code}</b>
                {dyn?.description ? <span> — {dyn.description}</span> : null}
                {': '}
                <span>
                  {gradings &&
                    Array.isArray(gradings) &&
                    gradings?.map((grad) => `${grad?.sizeName}: ${roundingOff(grad?.measurment, 2, true) || 0}`).join(', ')}
                  {`, Tol+/-: ${roundingOff(dyn?.tolMinus, 2, true) || 0}`}
                  {`, E-comm Spec: ${measurementUnit?.display?.label || 0}, ${roundingOff(measurementUnit?.measurment, 2, true) || '--'}`}
                  {`, Unit: ${dyn?.conversationType || '--'}`}
                </span>
                <br />
              </React.Fragment>
            );
          }
          return (
            <React.Fragment key={alignedRow.key}>
              <b>{dyn?.code || '--'}</b>
              {dyn?.description ? <span> — {dyn.description}</span> : null}
              <br />
            </React.Fragment>
          );
        })}
      </>
    );
  };

  /**
   * Renders a stored CAD File change. `patternData` is a snapshot object rather than a named
   * entity, so the generic object branch printed '--' for every such row.
   * @param {Object} fields one history field entry.
   * @returns {JSX.Element}
   */
  const renderCadFile = (fields) => {
    const entries = formatCadFileFields(fields?.[colType]);
    if (entries.length === 0) {
      return (
        <>
          <b>{startCase(fields?.field)}</b>: --
          <br />
        </>
      );
    }
    return (
      <>
        <b>{startCase(fields?.field)}</b>:{' '}
        {entries.map((entry, index) => (
          <React.Fragment key={entry.label}>
            {index > 0 ? ', ' : ''}
            <b>{entry.label}</b>: <span>{entry.value}</span>
          </React.Fragment>
        ))}
        <br />
      </>
    );
  };

  /**
   * Renders a stored Weight/Volume change. `weightVolData` is a list of per-size rows (a bare
   * object on pre-migration documents), so the generic object branch printed '--' for every
   * such row and a Weight/Volume-only edit read as an empty history entry.
   * @param {Object} fields one history field entry.
   * @returns {JSX.Element}
   */
  const renderWeightVolume = (fields) => {
    const rows = formatWeightVolumeRows(fields?.[colType]);
    if (rows.length === 0) {
      return (
        <>
          <b>{startCase(fields?.field)}</b>: --
          <br />
        </>
      );
    }
    return (
      <>
        <b>{startCase(fields?.field)}</b>:
        {rows.map((entries, rowIndex) => (
          <React.Fragment key={`weight-volume-${rowIndex}`}>
            <br />
            {entries.map((entry, index) => (
              <React.Fragment key={entry.label}>
                {index > 0 ? ', ' : ''}
                <b>{entry.label}</b>: <span>{entry.value}</span>
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
        <br />
      </>
    );
  };

  /**
   * Renders one side of a base-size change.
   *
   * A base-size move is the one Specifications edit that leaves every measurement untouched and
   * rewrites every grade rule, so the column has to carry the rules — measurements alone read
   * identically on both sides and the row says nothing. Both sides are walked in a single aligned
   * order, so line N of From and line N of To name the same POM and the change can be read across.
   * The record stores the whole grading group, most of which a given move may not touch, so the
   * POMs the change actually recalculated are counted and the untouched ones are marked as such.
   * @param {Object} fields one history field entry, carrying both lhs and rhs.
   * @returns {JSX.Element}
   */
  const renderBaseSizeRecalculation = (fields) => {
    const alignedRows = alignPomRowsByCode(fields?.lhs, fields?.rhs);
    if (alignedRows.length === 0) {
      return (
        <>
          <b>{getFieldDisplayName(fields?.field)}</b>: --
          <br />
        </>
      );
    }
    const recalculatedCount = alignedRows.filter((alignedRow) => hasGradingRunChanged(alignedRow.lhs, alignedRow.rhs)).length;
    return (
      <>
        <b>{getFieldDisplayName(fields?.field)}</b>
        <span className="font-13">{` (${recalculatedCount} of ${alignedRows.length} POMs recalculated)`}</span>
        {': '}
        {alignedRows.map((alignedRow) => {
          const pom = alignedRow[colType];
          if (!pom) {
            return (
              <React.Fragment key={alignedRow.key}>
                <b>--</b>
                <br />
              </React.Fragment>
            );
          }
          const gradingRun = formatBaseSizePomGradings(pom);
          return (
            <React.Fragment key={alignedRow.key}>
              <b>{pom?.code || '--'}</b>
              {pom?.description ? <span> — {pom.description}</span> : null}
              {gradingRun ? <span>: {gradingRun}</span> : null}
              {hasGradingRunChanged(alignedRow.lhs, alignedRow.rhs) ? null : <span className="font-13"> (unchanged)</span>}
              <br />
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const renderSampleSpecMeasurement = (fields) => {
    const data = fields[colType];
    const hasRows = Array.isArray(data) && data.length > 0;

    if (!fields.pomCode && !hasRows) {
      return '--';
    }

    return (
      <>
        <b>POM: {fields.pomCode || '--'}</b>
        {fields.description && <div className="font-13">{fields.description}</div>}
        {hasRows && (
          <table className="table mb-0 mt-1 font-13 spec-measurement-history-table">
            <thead>
              <tr>
                <th className="py-0 px-1">Size</th>
                <th className="py-0 px-1">Field</th>
                <th className="py-0 px-1">Value</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={`${row.sizeName ?? ''}-${row.fieldName ?? ''}-${idx}`}>
                  <td className="py-0 px-1">{row.sizeName || '--'}</td>
                  <td className="py-0 px-1">{row.fieldName || '--'}</td>
                  <td className="py-0 px-1">{row.value || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  };

  const renderMainTypeBased = (fields) => {
    if (fields?.field === 'data') {
      return <div className="px-2 py-1">{renderData(fields)}</div>;
    }
    if (fields?.field === 'Image' || fields?.field === 'images' || fields?.field === 'annotatedImageurl') {
      return <div className="px-2 py-1">{renderImage(fields)}</div>;
    }
    if (fields?.field === 'pdf') {
      return <div className="px-2 py-1">{renderPdf(fields)}</div>;
    }
    if (fields?.field === 'Tracking Dates') {
      return <div className="px-2 py-1">{renderTrackingDates(fields)}</div>;
    }
    if (fields?.field === 'Colourways') {
      return <div className="px-2 py-1">{renderColourways(fields)}</div>;
    }
    if (fields?.field === 'Bom Component') {
      return <div className="px-2 py-1">{renderBomCompoentn(fields)}</div>;
    }
    if (fields?.field === 'Product Identifier') {
      return <div className="px-2 py-1">{renderProductIndentifier(fields)}</div>;
    }
    if (fields?.field === 'Composition') {
      return <div className="px-2 py-1">{renderComposition(fields)}</div>;
    }
    if (fields?.field === 'Dynamic Column') {
      return <div className="px-2 py-1">{renderDynamicColumn(fields)}</div>;
    }
    if (fields?.field === 'Spec Pom Libraries' || fields?.field === 'Pom Libraries') {
      return <div className="px-2 py-1">{renderSpecPom(fields)}</div>;
    }
    if (fields?.field === 'Comment') {
      return <div className="px-2 py-1">{renderComment(fields)}</div>;
    }
    if (fields?.field === 'variants') {
      return <div className="px-2 py-1">{renderVariants(fields)}</div>;
    }
    if (fields?.field === 'Columns') {
      return <div className="px-2 py-1">{renderVariants(fields)}</div>;
    }
    if (fields?.field === 'Manual Columns') {
      return <div className="px-2 py-1">{renderManualVariants(fields)}</div>;
    }
    if (fields?.field === 'Sample Spec Measurement') {
      return <div className="px-2 py-1">{renderSampleSpecMeasurement(fields)}</div>;
    }
    if (fields?.field === SPEC_POM_LABEL || fields?.field === CAD_FILE_POM_LABEL || BASE_SIZE_POM_FIELDS.includes(fields?.field)) {
      return <div className="px-2 py-1">{renderBaseSizeRecalculation(fields)}</div>;
    }
    if (fields?.field === 'CAD File') {
      return <div className="px-2 py-1">{renderCadFile(fields)}</div>;
    }
    if (fields?.field === 'Weight/Volume') {
      return <div className="px-2 py-1">{renderWeightVolume(fields)}</div>;
    }
  };

  const renderStringBool = (fields) => {
    const value = fields[colType];
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;

    let displayValue;

    if (typeof value === 'string' && htmlRegex.test(value)) {
      displayValue = <span dangerouslySetInnerHTML={{ __html: value }} />;
    } else if (isIsoDate(value)) {
      displayValue = moment(value).format(dateFormat);
    } else {
      displayValue = value?.toString() || '--';
      const rawString = value?.toString?.() || '';
      const normalizedField = fields?.field?.toLowerCase?.() || '';
      if (
        normalizedField === 'description' ||
        normalizedField === 'additional information' ||
        normalizedField === 'min pass rate' ||
        normalizedField === 'pdfname' ||
        normalizedField === 'pdf'
      ) {
        displayValue = rawString ? shortenandHoverString(rawString, 50, 'auto') : '--';
      } else {
        displayValue = rawString || '--';
      }
    }

    return (
      <>
        {fields.field !== 'templateJsonSchema' && <b className="text-bold">{getFieldDisplayName(fields.field)}: </b>}
        {displayValue}
        <br />
      </>
    );
  };

  const renderArray = (fields) => {
    const value = fields[colType];
    // Handle single object values (like factory)
    if (value && typeof value === 'object' && !isArray(value)) {
      let displayValue = '--';
      if (fields?.field === 'Factory') {
        displayValue = value?.factoryName || '--';
      } else if (value?.name) {
        displayValue = value?.name;
      }
      return (
        <>
          <b className="text-bold">{startCase(fields.field)}</b>: {displayValue}
          <br />
        </>
      );
    }

    return (
      <>
        <b className="text-bold">{startCase(fields.field)}</b>:{' '}
        {value
          ?.map((val) => {
            if (val?.name) return val?.name;
            if (typeof val === 'string') return val;
            if (fields?.field === 'Factory') return val?.factoryName;
            if (fields?.field === 'gradingGroupData') {
              const name = val?.gradingGroup?.name || '--';
              const size = val?.size ? `, (${val.size})` : '';
              return `${name}${size}`;
            }
            return '--';
          })
          .join(', ')}
        <br />
      </>
    );
  };
  const renderCustomValue = (column) => {
    let selectedNames;
    let selectedMulti;
    switch (column.key) {
      case 'Dropdown':
        return column?.value?.name || column?.value?.label;
      case 'Multi_Dropdown':
      case 'Label':
        selectedMulti = column?.value?.map((item) => item?.label);
        // Join the names with ", "
        return selectedMulti?.join(', ');
      case 'Text':
      case 'Text_Area':
      case 'Paragraph':
      case 'Date_Picker':
      case 'Number_Percentage':
      case 'Number_Decimal':
        return column?.value;
      case 'Signature':
        if (column?.digitalSignPath !== '') {
          if (column?.digitalSignPath.includes('base64')) {
            return <img src={column?.digitalSignPath} width={50} alt="" />;
          }
          return <img src={`${imageUrl}/${column?.digitalSignPath}`} width={100} alt="" />;
        }
        return <span style={{ fontFamily: column?.fontFamily }}>{column?.placeholder}</span>;

      case 'Check_Boxes':
      case 'Ratio_Button':
        selectedNames = column?.options.filter((item) => item?.value === true || item?.isChecked === true).map((item) => item?.name || item?.label);
        // Join the names with ", "
        return selectedNames?.join(', ');
      case 'Time_Picker':
        return moment(column?.value).format('HH:mm');
      case 'Status':
        return column?.statusColours?.filter((item) => item?.setDefault).map((item) => item?.colourName);
      case 'Company_Stamp':
        if (column?.companyLogo.includes('base64')) {
          return <img src={column?.companyLogo} width={50} alt="" />;
        }
        return <img src={`${imageUrl}/${column?.companyLogo}`} width={50} alt="" />;
      default:
        return '--';
    }
  };

  const renderCustomControl = (fields) => {
    return fields?.map((val) => {
      return (
        <div key={val.label}>
          {(val?.key === 'Section' || val?.key === 'Table') && <h6 className="border-bottom font-weight-bold">{startCase(val.label)}</h6>}

          {val.columns?.map((column1) => {
            const filteredData = column1.filter((k) => k != null && k.key !== 'Div');

            return filteredData?.map((column) => {
              if (column.key === 'Div') {
                return null;
              }
              return (
                <p className="my-1" key={column.label}>
                  <b>{column?.label}</b>: {renderCustomValue(column)}
                </p>
              );
            });
          })}
          <table className="w-100">
            {val.tableData?.map((column1) => {
              const filteredData = column1.filter((k) => k != null && k.key !== 'Div');
              return (
                <tbody key={column1} style={{ borderBottom: '2px solid #848484' }}>
                  {filteredData?.map((column) => {
                    if (column.key === 'Div') {
                      return null;
                    }
                    return (
                      <tr key={column.label}>
                        <td className="p-1">
                          <b>{column?.fieldName}</b>: {renderCustomValue(column)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
        </div>
      );
    });
  };
  return (
    <span>
      {isArray(historyFieldsData) && historyFieldsData.length > 0
        ? historyFieldsData.map((fields, fieldIndex) => {
            if (
              [
                'Product Identifier',
                'Dynamic Column',
                'Composition',
                'Pom Libraries',
                'Spec Pom Libraries',
                'Bom Component',
                'Tracking Dates',
                'Colourways',
                'Image',
                'data',
                'pdf',
                'Comment',
                'variants',
                'Columns',
                'Manual Columns',
                'numberOfImages',
                'images',
                'annotatedImageurl',
                'Sample Spec Measurement',
                SPEC_POM_LABEL,
                CAD_FILE_POM_LABEL,
                ...BASE_SIZE_POM_FIELDS,
                'CAD File',
                'Weight/Volume',
              ].includes(fields?.field)
            ) {
              return renderMainTypeBased(fields);
            }
            if (typeof fields[colType] === 'string' || typeof fields[colType] === 'boolean') {
              return (
                <div key={`${fields?.field ?? 'field'}-${fieldIndex}`} className="px-2 py-1">
                  {renderStringBool(fields)}
                </div>
              );
            }
            if (!isArray(fields[colType]) && typeof fields[colType] === 'object') {
              return (
                <div key={`${fields?.field ?? 'field'}-${fieldIndex}`} className="px-2 py-1">
                  <b>{startCase(fields.field)}</b>: {fields[colType]?.name || '--'}
                  <br />
                </div>
              );
            }
            if (fields?.field === 'templateJsonSchema') {
              return (
                <div key={`${fields?.field ?? 'field'}-${fieldIndex}`} className="position-static">
                  <PerfectScrollbar className="inner-scroll">{renderCustomControl(fields[colType])}</PerfectScrollbar>
                </div>
              );
            }
            if (isArray(fields[colType])) {
              return (
                <div key={`${fields?.field ?? 'field'}-${fieldIndex}`} className="px-2 py-1">
                  {renderArray(fields)}
                </div>
              );
            }
            if (fields?.field === 'numberOfImages') {
              return (
                <div key={`${fields?.field ?? 'field'}-${fieldIndex}`} className="px-2 py-1">
                  <b>{t('PRODUCT_HISTORY.ORIENTATION')}</b>: {fields[colType]} Columns
                </div>
              );
            }

            return (
              <div key={`${fields?.field ?? 'field'}-${fieldIndex}`} className="px-2 py-1">
                <b>{startCase(fields.field)}</b>: --
              </div>
            );
          })
        : '--'}
    </span>
  );
}

export default DevelopmenorderFields;
